export interface SceneStat {
    label: string
    value: number
    decimals: number
    suffix: string
}

export interface SceneFlag {
    id: string
    text: string
    anchor: string
    leader: number
    dots?: boolean
}

export interface HomeScene {
    number: string
    tab: string
    tag: string
    figure: string
    grid: string
    headline: Array<string>
    body: string
    stats: Array<SceneStat>
    flags: Array<SceneFlag>
    description: string
    link: {
        href: string
        label: string
    }
}

export const HOME_SCENES: Array<HomeScene> = [
    {
        number: '01',
        tab: 'Capture',
        tag: '(01) Capture / Project / Live lidar',
        figure: 'Fig. 01 / Hallmark Library / Point cloud',
        grid: 'Local grid / 1 : 400',
        headline: ['The ground truth', 'for every building.'],
        body: 'Norrvik scans, measures and models every structure we touch, then keeps the model alive after handover, measured to the millimetre.',
        stats: [
            { label: 'Points captured', value: 318420000, decimals: 0, suffix: '' },
            { label: 'Coverage', value: 100, decimals: 1, suffix: '%' },
            { label: 'Floor area', value: 9400, decimals: 0, suffix: ' m²' }
        ],
        flags: [
            { id: 'FLAG-01', text: 'Slab deflection · Δ 11 mm', anchor: 'flag1', leader: 64 },
            { id: 'FLAG-02', text: 'Facade moisture', anchor: 'flag2', leader: 92 },
            { id: 'FLAG-03', text: 'Beam check · Δ 4 mm', anchor: 'flag3', leader: 58 }
        ],
        description: 'Point cloud of Hallmark Library in Umeå: a timber library with a rectangular footprint, a stepped roof and an exposed grid of glulam columns and beams, set in a ring of scanned ground. A vertical scan plane sweeps across the building while the drone UAV-02 hovers above it. Flags mark an 11 millimetre slab deflection, facade moisture and a 4 millimetre beam check.',
        link: { href: '/live-model?building=hallmark-library', label: 'Explore a live model' }
    },
    {
        number: '02',
        tab: 'Energy',
        tag: '(02) Energy / Project / Thermal twin',
        figure: 'Fig. 02 / Casa Mares / Thermal twin',
        grid: 'Local grid / 1 : 250',
        headline: ['Every window,', 'accounted for.'],
        body: 'Heat, daylight and carbon are tracked per facade, per hour, so the building is tuned against what actually happens.',
        stats: [
            { label: 'Homes tagged', value: 64, decimals: 0, suffix: '' },
            { label: 'Facade deviation', value: 2.1, decimals: 1, suffix: ' mm' },
            { label: 'Audit cycle', value: 14, decimals: 0, suffix: ' days' }
        ],
        flags: [
            { id: 'FL-01', text: 'Thermal bridge · 1.9 W/m²K', anchor: 'flag1', leader: 70 },
            { id: 'FL-02', text: 'Solar gain · 38°', anchor: 'flag2', leader: 54 },
            { id: 'FL-03', text: 'Seal failure', anchor: 'flag3', leader: 84 }
        ],
        description: 'Thermal model of Casa Mares in Lisbon: 64 homes drawn as stacked floor slabs with vertical facade fins, coloured from cool grey to blue by heat loss, with two hot spots on the west facade and a dotted sun path arcing above. Flags mark a thermal bridge of 1.9 watts per square metre kelvin, solar gain at 38 degrees and a failed window seal.',
        link: { href: '/live-model?building=casa-mares', label: 'Explore a live model' }
    },
    {
        number: '03',
        tab: 'Living',
        tag: '(03) Living / Project / Occupancy',
        figure: 'Fig. 03 / Ostra Hall / Occupancy model',
        grid: 'Local grid / 1 : 200',
        headline: ['Rooms,', 'measured in use.'],
        body: 'Occupancy, air quality and acoustics stream into the model so a space is judged by how it is lived in.',
        stats: [
            { label: 'People tracked', value: 1240, decimals: 0, suffix: '' },
            { label: 'Peak occupancy', value: 86, decimals: 0, suffix: '%' },
            { label: 'Avg dwell', value: 41, decimals: 0, suffix: ' min' }
        ],
        flags: [
            { id: 'ZN-02', text: 'Queue forming', anchor: 'flag1', leader: 78 },
            { id: 'ZN-04', text: 'CO2 940 ppm', anchor: 'flag2', leader: 60 },
            { id: 'ZN-01', text: 'Door 3 blocked', anchor: 'flag3', leader: 96 }
        ],
        description: 'Occupancy model of Ostra Cultural Hall in Gdańsk: the hall interior with a tiered auditorium, a lobby and a steel roof truss. Small moving dots are people drifting along the circulation paths and gathering at the doors. Flags mark a queue forming in zone 2, carbon dioxide at 940 parts per million in zone 4 and door 3 blocked.',
        link: { href: '/live-model?building=ostra-cultural-hall', label: 'Explore a live model' }
    },
    {
        number: '04',
        tab: 'Archive',
        tag: '(04) Archive / Studio / Cold storage',
        figure: 'Fig. 04 / Studio index / Cold storage',
        grid: 'Index grid / 72 × 72',
        headline: ['Indexed.', 'Queryable.', 'Kept for decades.'],
        body: 'Every drawing, scan and decision is stored in one archive, searchable in milliseconds, readable in fifty years.',
        stats: [
            { label: 'Archive size', value: 2.3, decimals: 1, suffix: ' PB' },
            { label: 'Query p95', value: 11, decimals: 0, suffix: ' ms' },
            { label: 'Retention', value: 25, decimals: 0, suffix: ' yr' }
        ],
        flags: [
            { id: 'IDX-7F', text: 'Largest partition · 2.3 PB', anchor: 'flag1', leader: 72 },
            { id: 'REPL', text: '3 sites · in sync', anchor: 'flag2', leader: 58, dots: true }
        ],
        description: 'The studio archive as a lattice of 72 by 72 thin columns of points, one column for each project record from 2009 to 2026, rising across the grid like a landscape and glowing cream at the tops. Small star clusters pulse, new records flash as they are written and query ripples spread from single columns. Flags mark the largest partition at 2.3 petabytes, queries answered in 11 milliseconds and replication across three sites.',
        link: { href: '/live-model', label: 'Explore a live model' }
    }
]

export const QUERY_FLAG_TEXT = 'Answered in 11 ms'

export const AUTOPLAY_SECONDS = 9
